import {CustomSession} from "@/components/Nav/Session";
import {AdminPanelSettings, Notifications} from "@mui/icons-material";
import {
	Badge,
	Box,
	IconButton,
	LinearProgress,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Popover,
	Typography
} from "@mui/material";
import {useSession} from "@toolpad/core";
import { ThemeSwitcher } from "@/components/ThemeProvider/ThemeSwitcher";
import Link from "next/link";
import React, {useState} from "react";
import {useNotifications} from "@/contexts/NotificationContext";
import PopoverEvents from "@/components/Events/PopoverEvents";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import {useAdminAccess} from "@/hooks/useAdminAuth";

export default function Notification({ user }) {
	const session = useSession<CustomSession>();
	const [notifsAnchorEl, setNotifsAnchorEl] = useState<null | HTMLElement>(null);
	const notifsOpen = Boolean(notifsAnchorEl);

	const { isAdmin, loadingNav, availableNavItems } = useAdminAccess();
	const isLoggedIn = session !== null ? true : false;
	const [adminAnchorEl, setAdminAnchorEl] = useState<null | HTMLElement>(null);
	const adminOpen = Boolean(adminAnchorEl);

	const { unreadCount, markAllAsRead } = useNotifications();

	const handleNotifsClick = (event: React.MouseEvent<HTMLElement>) => {
		setNotifsAnchorEl(event.currentTarget);
	};

	const handleNotifsClose = () => {
		setNotifsAnchorEl(null);
	};

	const handleAdminClick = (event: React.MouseEvent<HTMLElement>) => {
		setAdminAnchorEl(event.currentTarget);
	};

	const handleAdminClose = () => {
		setAdminAnchorEl(null);
	};

	const handleMarkAllRead = async () => {
		await markAllAsRead();
	};

	return (
		<>
			<Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.25, sm: 1 } }}>
				{isLoggedIn && (
					<IconButton onClick={handleNotifsClick} color="primary" sx={{ padding: { xs: "6px", sm: "12px" } }}>
						<Badge badgeContent={unreadCount} color="error">
							<Notifications />
						</Badge>
					</IconButton>
				)}

				{isAdmin && isLoggedIn && (
					<>
						<IconButton onClick={handleAdminClick} color="primary" sx={{ padding: { xs: "6px", sm: "12px" } }}>
							<AdminPanelSettings />
						</IconButton>
						<Popover
							anchorEl={adminAnchorEl}
							open={adminOpen}
							onClose={handleAdminClose}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "center",
							}}
							slotProps={{
								paper: {
									sx: {
										pb: 2,
									},
								},
							}}
						>
							<Typography fontWeight="bold" sx={{ p: 2 }}>
								Admin Panel
							</Typography>
							<nav>
								{!loadingNav && availableNavItems.map((nav, index) => (
									<Link
										key={index}
										href={`/${nav.segment}`}
										style={{
											textDecoration: "none",
											color: "inherit",
										}}
									>
										<MenuItem>
											<ListItemIcon>{nav.icon}</ListItemIcon>
											<ListItemText primary={nav.title} />
										</MenuItem>
									</Link>
								))}
								{loadingNav && <LinearProgress color="primary" />}
							</nav>
						</Popover>
					</>
				)}

        <Popover
          anchorEl={notifsAnchorEl}
          open={notifsOpen}
          onClose={handleNotifsClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          PaperProps={{
            sx: {
              height: "500px",
              width: { xs: "90vw", sm: "400px" },
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              boxShadow: 3,
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Notifications ({unreadCount})
            </Typography>
            {unreadCount > 0 && (
              <IconButton
                size="small"
                onClick={handleMarkAllRead}
                sx={{
                  color: "primary.main",
                  "&:hover": { bgcolor: "primary.lighter" },
                }}
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <PopoverEvents user={user} onNavigate={handleNotifsClose} />
          </Box>
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              position: "sticky",
              bottom: 0,
              zIndex: 1,
            }}
          >
            <Link
              href="/events"
              style={{ textDecoration: "none" }}
              onClick={handleNotifsClose}
            >
              <Typography
                color="primary"
                align="center"
                sx={{ fontWeight: 500 }}
              >
                View All Notifications
              </Typography>
            </Link>
          </Box>
        </Popover>
      </Box>
      {/* <ThemeSwitcher /> */}
    </>
  );
}
